import ServiceFeeNote from "@/components/ServiceFeeNote";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { getRef } from "@/lib/shareRef";
import { toast } from "sonner";
import { X, Plus, Minus, Trash2, Ticket, Truck } from "lucide-react";

const tid = (i) => i.key.replace(/[^a-zA-Z0-9]+/g, "-");

export default function CartDrawer() {
  const { user } = useAuth();
  const cart = useCart();
  const { list, count, shippableSubtotal, config, open, setOpen, changeQty, removeItem, clear } = cart;
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [shipTo, setShipTo] = useState(() => {
    try { return JSON.parse(localStorage.getItem("pb_ship_to")) || {}; } catch { return {}; }
  });
  const setAddr = (k, v) => setShipTo((s) => {
    const next = { ...s, [k]: v };
    localStorage.setItem("pb_ship_to", JSON.stringify(next));
    return next;
  });
  const sig = JSON.stringify(list.map((i) => [i.key, i.qty])) + "|" + (shipTo.state || "");
  useEffect(() => {
    if (!open || !list.length) { setQuote(null); return; }
    const t = setTimeout(() => {
      api.post("/cart/quote", { items: list.map((i) => ({ type: i.type, id: i.id, qty: i.qty, variant: i.variant || null })), ship_state: shipTo.state || null })
        .then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    }, 250);
    return () => clearTimeout(t);
  }, [sig, open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;
  const productSubtotal = quote?.product_subtotal ?? cart.productSubtotal;
  const passSubtotal = quote?.pass_subtotal ?? cart.passSubtotal;
  const tax = quote?.tax ?? cart.tax;
  const shipping = quote?.shipping ?? cart.shipping;
  const total = quote?.total ?? cart.total;

  const freeShipGap = config.free_shipping_threshold - shippableSubtotal;
  const needsAddress = shippableSubtotal > 0;
  const addressComplete = ["name", "street", "city", "state", "zip"].every((k) => (shipTo[k] || "").trim());

  const checkout = async () => {
    if (needsAddress && !addressComplete) return toast.error("Fill in your shipping address — your cart has physical items");
    setBusy(true);
    try {
      const { data } = await api.post("/cart/checkout", {
        items: list.map((i) => ({ type: i.type, id: i.id, qty: i.qty, variant: i.variant || null })),
        ref: getRef(user?.id),
        origin_url: window.location.origin,
        ship_to: needsAddress ? shipTo : null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex justify-end" onClick={() => setOpen(false)}>
      <div className="w-full max-w-md bg-pb-surface border-l border-pb-border h-full p-6 flex flex-col animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="global-cart-drawer">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-black text-2xl uppercase">Your Cart</h3>
          <button data-testid="cart-close-btn" onClick={() => setOpen(false)}><X className="w-5 h-5 text-zinc-500 hover:text-white" /></button>
        </div>

        {list.length === 0 ? (
          <div className="text-zinc-600 text-sm flex-1" data-testid="cart-empty">Your cart is empty. Add gear from the Store or session passes from Book.</div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4">
            {list.map((i) => (
              <div key={i.key} data-testid={`cart-item-${tid(i)}`} className="flex gap-3 items-center border-b border-pb-border/50 pb-4">
                {i.image ? (
                  <img src={i.image} alt={i.name} className="w-14 h-14 object-cover" />
                ) : (
                  <div className="w-14 h-14 bg-pb-elevated flex items-center justify-center"><Ticket className="w-6 h-6 text-pb-cyan" /></div>
                )}
                <div className="flex-1">
                  <div className="font-semibold text-sm">{i.name}</div>
                  {i.variant_label && <div className="text-[10px] uppercase tracking-widest text-pb-cyan" data-testid={`cart-variant-${tid(i)}`}>{i.variant_label}</div>}
                  <div className="text-pb-red font-mono text-sm">${i.price.toFixed(2)}</div>
                  {i.type === "pass" && <span className="text-[10px] uppercase tracking-widest text-pb-cyan">Session Pass</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`cart-dec-${tid(i)}`} onClick={() => changeQty(i.key, -1)} className="border border-pb-border w-7 h-7 flex items-center justify-center hover:border-pb-red"><Minus className="w-3 h-3" /></button>
                  <span className="font-mono w-6 text-center">{i.qty}</span>
                  <button data-testid={`cart-inc-${tid(i)}`} onClick={() => changeQty(i.key, 1)} className="border border-pb-border w-7 h-7 flex items-center justify-center hover:border-pb-red"><Plus className="w-3 h-3" /></button>
                </div>
                <button data-testid={`cart-remove-${tid(i)}`} onClick={() => removeItem(i.key)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-pb-border pt-4 mt-4 space-y-2 text-sm">
          {needsAddress && (
            <div className="mb-2" data-testid="cart-ship-to">
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5"><Truck className="w-3 h-3" /> Ship To (required for physical items)</div>
              <div className="grid grid-cols-2 gap-1.5">
                <input data-testid="ship-name" value={shipTo.name || ""} onChange={(e) => setAddr("name", e.target.value)} placeholder="Full name"
                  className="col-span-2 bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
                <input data-testid="ship-street" value={shipTo.street || ""} onChange={(e) => setAddr("street", e.target.value)} placeholder="Street address"
                  className="col-span-2 bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
                <input data-testid="ship-city" value={shipTo.city || ""} onChange={(e) => setAddr("city", e.target.value)} placeholder="City"
                  className="bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
                <div className="grid grid-cols-2 gap-1.5">
                  <input data-testid="ship-state" value={shipTo.state || ""} onChange={(e) => setAddr("state", e.target.value)} placeholder="State"
                    className="bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
                  <input data-testid="ship-zip" value={shipTo.zip || ""} onChange={(e) => setAddr("zip", e.target.value)} placeholder="ZIP"
                    className="bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red" />
                </div>
              </div>
            </div>
          )}
          {shippableSubtotal > 0 && freeShipGap > 0 && (
            <div className="flex items-center gap-2 text-xs text-pb-cyan bg-pb-elevated border border-pb-border px-3 py-2 mb-2" data-testid="free-shipping-hint">
              <Truck className="w-4 h-4 shrink-0" /> Add ${freeShipGap.toFixed(2)} more in gear for FREE shipping
            </div>
          )}
          <div className="flex justify-between text-zinc-400">
            <span>Subtotal{passSubtotal > 0 ? " (gear + passes)" : ""}</span>
            <span className="font-mono" data-testid="cart-subtotal">${(productSubtotal + passSubtotal).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-zinc-400">
            <span data-testid="cart-tax-label">{quote ? `Sales tax${shipTo.state ? ` · ${shipTo.state.toUpperCase()}` : ""}` : `Tax (${Math.round(config.tax_rate * 100)}% on gear)`}</span>
            <span className="font-mono" data-testid="cart-tax">${tax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-zinc-400">
            <span>Shipping {shippableSubtotal === 0 ? "(no shippable items)" : "(est.)"}</span>
            <span className="font-mono" data-testid="cart-shipping">{shipping === 0 ? (shippableSubtotal >= config.free_shipping_threshold ? "FREE" : "$0.00") : `$${shipping.toFixed(2)}`}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-pb-border/50">
            <span className="uppercase tracking-widest text-zinc-500">Total</span>
            <span className="font-display font-black text-2xl text-pb-red" data-testid="cart-total">${total.toFixed(2)}</span>
          </div>
          <button data-testid="cart-checkout-btn" onClick={checkout} disabled={busy || count === 0}
            className="w-full bg-pb-red text-black font-bold uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-40 mt-2">
            {busy ? "Processing…" : "Checkout Securely"}
          </button>
          <ServiceFeeNote className="text-center mt-2" />
          <Link to="/return-policy" onClick={() => setOpen(false)} data-testid="cart-return-policy-link" className="block text-center mt-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-pb-cyan">30-day return policy</Link>
        </div>
      </div>
    </div>
  );
}
